import React, { useState } from 'react';

import {
  Table,
  TableBody,
  TableCell,
  TableSortLabel,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  CircularProgress,
  IconButton,
  Tooltip,
  makeStyles,
} from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import moment from 'moment';
import FileOverwriteDialog from './FileOverwriteDialog';
import { useOverwriteFile } from 'services/CustomDataService';

const useStyles = makeStyles((theme) => ({
  selectableRow: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  selectedRow: {
    backgroundColor: theme.palette.action.selected,
  },
}));

export const FileListTable = ({
  files,
  totalFiles,
  isLoading,
  selectable = false,
  selectedFileId = null,
  onFileSelect = () => {},
}) => {
  const classes = useStyles();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('created_at');
  const [overwriteTarget, setOverwriteTarget] = useState(null);

  const { mutateAsync: overwriteFile } = useOverwriteFile();

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleRowClick = (fileId) => {
    if (selectable) {
      onFileSelect(fileId);
    }
  };

  const handleOverwriteClick = (e, file) => {
    e.stopPropagation();
    setOverwriteTarget(file);
  };

  const handleOverwriteConfirm = async (newFile) => {
    await overwriteFile({ userFileId: overwriteTarget.user_file_id, file: newFile });
  };

  if (isLoading) {
    return <CircularProgress />;
  }

  const displayFiles = files
    ?.sort((a, b) => {
      const sortOrder = order === 'asc' ? 1 : -1;
      return sortOrder * (a[orderBy] < b[orderBy] ? -1 : a[orderBy] > b[orderBy] ? 1 : 0);
    })
    ?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>
              <TableSortLabel
                active={orderBy === 'file_name'}
                direction={orderBy === 'file_name' ? order : 'asc'}
                onClick={() => handleRequestSort('file_name')}
              >
                Name
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={orderBy === 'created_at'}
                direction={orderBy === 'created_at' ? order : 'asc'}
                onClick={() => handleRequestSort('created_at')}
              >
                Uploaded
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={orderBy === 'updated_at'}
                direction={orderBy === 'updated_at' ? order : 'asc'}
                onClick={() => handleRequestSort('updated_at')}
              >
                Updated
              </TableSortLabel>
            </TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {displayFiles?.map((file) => (
            <TableRow
              key={file.user_file_id}
              onClick={() => handleRowClick(file.user_file_id)}
              className={`
                ${selectable ? classes.selectableRow : ''}
                ${selectable && file.user_file_id === selectedFileId ? classes.selectedRow : ''}
              `}
            >
              <TableCell>{file.file_name}</TableCell>
              <TableCell>{moment(file.created_at).format('MM-DD-YYYY HH:mm')}</TableCell>
              <TableCell>{moment(file.updated_at).format('MM-DD-YYYY HH:mm')}</TableCell>
              <TableCell align="right">
                <Tooltip title="Overwrite file">
                  <IconButton size="small" onClick={(e) => handleOverwriteClick(e, file)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <TablePagination
        rowsPerPageOptions={[10, 25, 50]}
        component="div"
        count={totalFiles || files?.length || 0}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
      <FileOverwriteDialog
        isOpen={!!overwriteTarget}
        onClose={() => setOverwriteTarget(null)}
        onOverwrite={handleOverwriteConfirm}
        existingFileName={overwriteTarget?.file_name ?? ''}
      />
    </TableContainer>
  );
};
